/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    useMultiStyleConfig,
    Heading,
    createStylesContext,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    Image,
    useDisclosure,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'
import Link from '@salesforce/retail-react-app/app/components/link'

import ContentAsset from '../content-asset'
import {getUrl} from '../../utils/cloudinary-utils'
import {SkeletonCircle} from '@chakra-ui/react'
import useTagManager from '../../hooks/use-tag-manager'
import Pepperjamscript from '../pepperjam'
import {KS_PRIVACY_POLICY_URL, KS_TERMS_URL} from '../../constants'
import FooterSubscriptionFields from '../forms/footer-subscription-fields'
import {useForm} from 'react-hook-form'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import useCountryDetails from '../../hooks/use-country-details'
import {useSiteContext} from '../../contexts/site'
import {useGlobaleContext} from '../../contexts/globale'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {shippingSwitcherConfigs} from '../../utils/common-utils'

const [StylesProvider, useStyles] = createStylesContext('Footer')

const footerLinksSkeleton = () => {
    return (
        <Box>
            <Skeleton
                width={{base: '100px', md: '70px'}}
                height="25px"
                marginBottom="10px"
                marginTop={{base: '10px', md: 0}}
                marginLeft={{base: '10px', md: 0}}
            />
            <Skeleton
                width="100px"
                height="2px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
            <Skeleton
                width="70px"
                height="15px"
                marginBottom="10px"
                display={{base: 'none', md: 'block'}}
            />
        </Box>
    )
}

const philanthropySkeleton = () => {
    return (
        <Box margin="10px 0">
            <Skeleton
                width="70px"
                height="25px"
                marginBottom="10px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width={{base: '70%', lg: '100%'}}
                height="15px"
                marginBottom="5px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
            <Skeleton
                width="30%"
                height="15px"
                marginLeft={{base: 'auto', lg: '0'}}
                marginRight={{base: 'auto', lg: '0'}}
            />
        </Box>
    )
}

const socialIconsSkeleton = () => {
    return (
        <Box display="inline-flex">
            <SkeletonCircle size="20px" marginRight="25px" />
            <SkeletonCircle size="20px" marginRight="25px" />
            <SkeletonCircle size="20px" marginRight="25px" />
            <SkeletonCircle size="20px" marginRight="25px" />
            <SkeletonCircle size="20px" marginRight="25px" />
            <SkeletonCircle size="20px" />
        </Box>
    )
}

const legalLinksSkeleton = () => {
    return (
        <Box display="block">
            <Skeleton width="70%" height="15px" margin="0 auto" />
        </Box>
    )
}

const Footer = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Footer')
    const {location, pathname} = useLocation()
    const appOrigin = getAppOrigin()
    const {isYR} = useSiteContext()
    const {setCurrencyContext, setCurrencyForGlobale, setCountryForGlobale, setCountryISOContext} =
        useGlobaleContext()
    const {
        app: {commerceAPI}
    } = getConfig()
    const tagManager = useTagManager()
    const isPDP = /\/\d+\.html$/.test(pathname)

    const {globalE} = commerceAPI
    const {config} = globalE || {}

    var geEnabled = false
    var geApiVersion = '2.1.4' // hardcode the default value
    try {
        if (config) {
            const jsonConfig = JSON.parse(config)
            geEnabled = jsonConfig.enabled
            geApiVersion = jsonConfig.apiVersion
        }
    } catch (e) {
        console.log(e.message)
    }

    const {countryISO} = useGlobaleContext()
    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    const {data: countryData} = useCountryDetails(countryISO, {enabled: geEnabled})

    useEffect(() => {
        if (!geEnabled) {
            return
        }

        const timer = setTimeout(() => {
            // addressing the edge case. i.e. set a time out of 5 seconds so that we'll not see skeleton, spinners on the site, specifically on the cart page
            if (!countryData) {
                console.warn(
                    'countryData not available after 5 seconds, falling back to US defaults'
                )
                setCurrencyContext('USD')
                setCurrencyForGlobale('USD')
                setCountryForGlobale('US')
                setCountryISOContext('US')
            }
        }, 5000)

        return () => clearTimeout(timer)
    }, [countryData, geEnabled])

    useEffect(() => {
        if (!countryData) {
            return
        }

        if (countryData?.currencyCode) {
            cookie.set('currencyCode', countryData?.currencyCode)
            cookie.set('cultureCode', countryData?.cultureCode)
            setCurrencyContext(countryData?.currencyCode)
        }

        try {
            /* 
                NOTE: Globale does not set this cookie INITIALLY. We must set it on the client side the first time.
                If we don’t set this cookie initially, Globale will throw errors/exceptions in the console. 

                We also need this to open the Globale Welcome modal.
            */
            var cookieValue = {
                cultureCode: countryData?.cultureCode?.toString() || 'en-GB',
                countryISO: countryISO,
                apiVersion: geApiVersion,
                currencyCode: countryData?.currencyCode?.toString()
            }

            document.cookie = `GlobalE_Data=${encodeURIComponent(
                JSON.stringify(cookieValue)
            )};domain=${'.' + appOrigin?.split('https://')?.[1]};path=/`
            const interval = setInterval(() => {
                if (window?.GlobalE) {
                    window.GlobalE.GetFreeShippingBanner()

                    const el = document.querySelector('.globale-box')
                    if (el) {
                        el.classList.remove('hide-element')
                    }
                    shippingSwitcherConfigs(
                        isYR,
                        cookieValue.countryISO,
                        cookieValue.cultureCode,
                        cookieValue.currencyCode
                    )
                    clearInterval(interval)
                }
            }, 100)

            return () => clearInterval(interval)
        } catch (e) {
            console.log(e.message)
        }
    }, [countryData?.currencyCode])

    useEffect(() => {
        document.addEventListener('click', function (event) {
            // Check if the clicked element has the 'footer-title' class
            if (event.target.classList.contains('footer-title')) {
                // Close all other open drawers
                document.querySelectorAll('.footer-title.open-drawer').forEach((title) => {
                    if (title !== event.target) {
                        title.classList.remove('open-drawer')
                        const ul = title.nextElementSibling
                        if (ul && ul.tagName.toLowerCase() === 'ul') {
                            ul.classList.remove('show')
                        }
                    }
                })

                // Toggle the clicked footer-title and its corresponding <ul>
                const nextUl = event.target.nextElementSibling
                if (nextUl && nextUl.tagName.toLowerCase() === 'ul') {
                    const isOpen = nextUl.classList.contains('show')

                    // Close if it's already open, otherwise open it
                    if (isOpen) {
                        event.target.classList.remove('open-drawer')
                        nextUl.classList.remove('show')
                        tagManager.newsletterFbSubscribe()
                    } else {
                        event.target.classList.add('open-drawer')
                        nextUl.classList.add('show')
                    }
                }
            }
        })
    }, [])
    const footerLinkClick = (e) => {
        const linkText = e.target.getAttribute('title')
        const headingText = e.currentTarget.querySelector('.footer-title').innerHTML
        if (linkText && headingText) {
            tagManager.navigationMain('footer', linkText, undefined, headingText)
        }
    }
    return (
        <Box as="footer" {...styles.container} {...otherProps} className="footer-container">
            <Box as="section" sx={isPDP ? styles.contentPDP : styles.content}>
                <StylesProvider value={styles}>
                    <Box
                        display="flex"
                        gap={{base: '0', lg: '30px'}}
                        flexWrap={{base: 'wrap', lg: 'nowrap'}}
                    >
                        <Box
                            width={{base: '100%', lg: '70%'}}
                            display={{base: 'grid', md: 'flex'}}
                            gap={{base: 0, md: '30px'}}
                            order={{base: 2, lg: 1}}
                        >
                            <Box
                                width={{lg: '25%', md: '25%', base: '100%'}}
                                borderBottom={{base: '1px solid', md: 0}}
                                borderColor="gray.550"
                                sx={{
                                    '& .title-l': {
                                        padding: {base: '0 15px 12px 15px', md: '0 0 11px 0'}
                                    },
                                    '& button': {
                                        display: 'none'
                                    }
                                }}
                                className="footer-links-asset"
                                onClick={footerLinkClick}
                            >
                                <ContentAsset
                                    contentAssetId={isYR ? 'yr-footer-col-1' : 'footer-col-1'}
                                    skeleton={footerLinksSkeleton}
                                />
                            </Box>
                            <Box
                                width={{lg: '25%', md: '25%', base: '100%'}}
                                borderBottom={{base: '1px solid', md: 0}}
                                borderColor="gray.550"
                                className="footer-links-asset"
                                onClick={footerLinkClick}
                                sx={{
                                    '& button': {
                                        display: 'none'
                                    }
                                }}
                            >
                                <ContentAsset
                                    contentAssetId={isYR ? 'yr-footer-col-2' : 'footer-col-2'}
                                    skeleton={footerLinksSkeleton}
                                />
                            </Box>
                            <Box
                                width={{lg: '25%', md: '25%', base: '100%'}}
                                borderBottom={{base: '1px solid', md: 0}}
                                borderColor="gray.550"
                                className="footer-links-asset"
                                onClick={footerLinkClick}
                                sx={{
                                    '& button': {
                                        display: 'none'
                                    }
                                }}
                            >
                                <ContentAsset
                                    contentAssetId={isYR ? 'yr-footer-col-3' : 'footer-col-3'}
                                    skeleton={footerLinksSkeleton}
                                />
                            </Box>
                            <Box
                                width={{lg: '25%', md: '25%', base: '100%'}}
                                borderBottom={{base: '1px solid', md: 0}}
                                borderColor="gray.550"
                                className="footer-links-asset"
                                onClick={footerLinkClick}
                                sx={{
                                    '& button': {
                                        display: 'none'
                                    }
                                }}
                            >
                                <ContentAsset
                                    contentAssetId={isYR ? 'yr-footer-col-4' : 'footer-col-4'}
                                    skeleton={footerLinksSkeleton}
                                />
                            </Box>
                        </Box>
                        <Box
                            width={{base: '100%', lg: '30%'}}
                            marginLeft={{lg: 'auto'}}
                            order={{base: 1, lg: 2}}
                            marginBottom={{base: '30px', lg: 0}}
                        >
                            <Subscribe
                                countryData={countryData}
                                geEnabled={geEnabled}
                                isYR={isYR}
                            />
                        </Box>
                    </Box>
                    <Box
                        className="footer-philanthropy-wrapper"
                        display={{base: 'block', lg: 'none'}}
                        borderTop="0"
                        textAlign="center"
                        margin={{md: '30px auto 40px', base: '30px 45px'}}
                    >
                        <ContentAsset
                            contentAssetId={
                                isYR
                                    ? 'yr-philanthropy-shine-bright-do-good-footer'
                                    : 'philanthropy-shine-bright-do-good-footer'
                            }
                            skeleton={philanthropySkeleton}
                        />
                    </Box>
                    <Box
                        className="social-icons-wrapper"
                        display={{base: 'block', lg: 'none'}}
                        textAlign="center"
                    >
                        <ContentAsset
                            contentAssetId={isYR ? 'yr-footer-social-icons' : 'footer-social-icons'}
                            skeleton={socialIconsSkeleton}
                        />
                    </Box>
                    {geEnabled && (
                        <Box display={{base: 'block', lg: 'none'}} textAlign="center">
                            <CountrySelector countryData={countryData} />
                        </Box>
                    )}
                    <Box
                        marginLeft={{base: '45px', lg: 'auto'}}
                        marginRight={{base: '45px', lg: 'auto'}}
                        marginTop={{base: '30px', lg: '50px'}}
                        className="legal-section-wrapper"
                    >
                        <ContentAsset
                            contentAssetId={isYR ? 'yr-footer-legal' : 'footer-legal'}
                            skeleton={legalLinksSkeleton}
                        />
                    </Box>
                </StylesProvider>
                <Pepperjamscript location={location} />
            </Box>
        </Box>
    )
}

export default Footer

const CountrySelector = ({countryData}) => {
    const {isYR} = useSiteContext()
    return (
        <Box
            className="globale-selector"
            width="fit-content"
            cursor="pointer"
            onClick={() => {
                /** NOTE: Note a reactJS practice, but required to set data-brand on <html> for brand-specific UI. **/
                document
                    .querySelector('html')
                    .setAttribute('data-brand', isYR ? 'yellowrose' : 'kendrascott')
                window.GlobalE.ShippingSwitcher.Show()
            }}
            margin={{base: '30px 0 0 0', lg: '40px 0 0 0'}}
            display="inline-flex"
            gap="10px"
            alignItems="center"
            data-country-name={countryData?.countryName}
            key={countryData?.countryName}
        >
            <Image
                src={getUrl('UIImages/images/country-selector.svg')}
                width="22.5px"
                height="22.5px"
                alt="Country Selector"
            />
            {countryData ? (
                <Text
                    color="black"
                    fontSize="16px"
                    fontWeight="300"
                    fontFamily="body"
                    lineHeight="25px"
                >
                    {countryData?.countryName} | {countryData?.currencySymbol}
                </Text>
            ) : (
                <Box display="inline-flex">
                    <Skeleton width="70px" height="16px" />
                    <Box w="1px" h="16px" bg="black" margin="0 8px" />
                    <Skeleton width="16px" height="16px" />
                </Box>
            )}
        </Box>
    )
}

const Subscribe = ({countryData, geEnabled = true, isYR = false}) => {
    const styles = useStyles()
    const intl = useIntl()
    const tagManager = useTagManager()
    const location = useLocation()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [showLoadingCTA, setShowLoadingCTA] = useState()
    const form = useForm()
    const [showSuccessMessage, setShowSuccessMessage] = useState(false)

    useEffect(() => {
        // always refresh these states if the URL changes
        setShowSuccessMessage(false)
    }, [location])

    const submitSubscriptionForm = (data) => {
        const {email, zipCode} = data
        form.clearErrors()
        setShowLoadingCTA(true)
        setTimeout(() => {
            setShowLoadingCTA(false)
            setTimeout(() => {
                setShowSuccessMessage(true)
            }, 400)
        }, 1000)
        onOpen(true)
        window._ltk.Signup.SetValue('Footer', 'ltkEmail', email)
        window._ltk.Signup.SetValue('Footer', 'Zip', zipCode)
        window._ltk.Signup.SetValue('Footer', 'ltkSaved', true)
        window._ltk.Signup.Subscribe('Footer')
        tagManager.formFieldChange('footer-emailsignup-form', 'submit')
    }

    return (
        <Box {...styles.subscribe} maxWidth="100%" margin={{base: '0 15px 0 15px', md: 0}}>
            <Heading as="p" {...styles.subscribeHeading} marginBottom="10px">
                {showSuccessMessage
                    ? intl.formatMessage({
                          id: 'subscribe.success',
                          defaultMessage: "You're In!"
                      })
                    : intl.formatMessage({
                          id: 'subscribe.title',
                          defaultMessage: 'Get 15% off your next purchase.'
                      })}
            </Heading>
            {!showSuccessMessage ? (
                <Box
                    sx={{
                        '& input': {
                            padding: '25px 15px 10px 15px'
                        }
                    }}
                >
                    <form
                        id="footer-emailsignup-form"
                        name="footer-emailsignup-form"
                        onSubmit={form.handleSubmit(submitSubscriptionForm)}
                        data-testid="sf-footer-emailsignup-form"
                    >
                        <FooterSubscriptionFields form={form} />
                        <Box mt="15px" mb="15px">
                            <Text color="black.100" fontSize="13px">
                                <FormattedMessage
                                    id="footer.subscribe_terms_text"
                                    defaultMessage="By clicking Sign Up, you agree to Kendra Scott's <terms>Terms & Conditions</terms> and <policy>Privacy Policy</policy>."
                                    values={{
                                        policy: (chunks) => (
                                            <Link
                                                to={KS_PRIVACY_POLICY_URL}
                                                textDecoration={'underline'}
                                                target="_blank"
                                            >
                                                {chunks}
                                            </Link>
                                        ),

                                        terms: (chunks) => (
                                            <Link
                                                to={KS_TERMS_URL}
                                                target="_blank"
                                                textDecoration={'underline'}
                                            >
                                                {chunks}
                                            </Link>
                                        )
                                    }}
                                />
                            </Text>
                        </Box>
                        <Button
                            type="submit"
                            width={{base: '100%', md: '129px'}}
                            border="none"
                            cursor="pointer"
                            onClick={() => {
                                form.clearErrors('global')
                                setTimeout(() => {
                                    const emailValidationMsg =
                                        document.querySelector(
                                            '#footer-emailsignup-form input[type="email"]'
                                        ).validationMessage ||
                                        document.querySelector(
                                            '#footer-emailsignup-form #email-feedback'
                                        )?.innerText
                                    const zipCodeValidationMsg =
                                        document.querySelector(
                                            '#footer-emailsignup-form input[type="text"]'
                                        ).validationMessage ||
                                        document.querySelector(
                                            '#footer-emailsignup-form #zipCode-feedback'
                                        )?.innerText
                                    if (!form?.formState?.isValid) {
                                        if (zipCodeValidationMsg) {
                                            tagManager.loyaltyRegistrationError(
                                                zipCodeValidationMsg
                                            )
                                        }
                                        if (emailValidationMsg) {
                                            tagManager.loyaltyRegistrationError(emailValidationMsg)
                                        }
                                        var validationMessages = [
                                            emailValidationMsg,
                                            zipCodeValidationMsg
                                        ]
                                            .filter(Boolean)
                                            .join('; ')
                                        tagManager.errorDisplayed(
                                            'form validation',
                                            validationMessages,
                                            'newsletter subscription'
                                        )
                                    }
                                }, 600)
                            }}
                            isLoading={form.formState.isSubmitting || showLoadingCTA}
                            aria-label={intl.formatMessage({
                                id: 'footer.subscribe.button.sign_up',
                                defaultMessage: 'Sign Up'
                            })}
                            _hover={{
                                backgroundColor: 'purple.60'
                            }}
                            _focus={{
                                backgroundColor: 'purple.60'
                            }}
                            _active={{
                                backgroundColor: 'purple.60'
                            }}
                            backgroundColor="purple.80"
                            color="black.100"
                            height="56px"
                            letterSpacing="0.005em"
                            lineHeight="26px"
                            padding="15px 10px !important"
                            fontSize="1.0625rem"
                        >
                            {intl.formatMessage({
                                id: 'footer.subscribe.button.sign_up',
                                defaultMessage: 'Sign Up'
                            })}
                        </Button>
                    </form>
                </Box>
            ) : (
                <Box marginBottom={{lg: '3rem', base: '0'}}>
                    <Text color="black.100" fontSize="1rem" lineHeight="25px" fontWeight="300">
                        {intl.formatMessage({
                            id: 'subscribe.success.message',
                            defaultMessage:
                                'Look out for a welcome email with your exclusive 15% off code.'
                        })}
                    </Text>
                </Box>
            )}
            <Modal isOpen={isOpen}>
                <ModalOverlay background="blackAlpha600" />
                <ModalContent className="subscription-success-modal" borderRadius="0">
                    <Box
                        position="absolute"
                        top="26px"
                        cursor="pointer"
                        right="25px"
                        borderRadius="50%"
                        _hover={{
                            backgroundColor: 'gray.100'
                        }}
                        padding="15px"
                        transition="all, 0.3s ease"
                        onClick={onClose}
                    >
                        <Image
                            width="15px"
                            height="15px"
                            src={getUrl('UIImages/images/crossed-icon.svg')}
                        />
                    </Box>
                    <ModalBody
                        className="list-track-content"
                        padding="100px 40px 100px 40px"
                        margin="10px"
                        border="1px solid"
                        borderColor="#d7d7d7"
                    >
                        <Box className="success-wrapper" margin="100px 20px 100px 20px">
                            <ContentAsset
                                contentAssetId={
                                    isYR ? 'yr-success-email-signup' : 'success-email-signup'
                                }
                            />
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Box
                className="footer-philanthropy-wrapper"
                display={{base: 'none', lg: 'block'}}
                borderTop="0.5px solid"
                margin="30px auto 30px"
            >
                <ContentAsset
                    contentAssetId={
                        isYR
                            ? 'yr-philanthropy-shine-bright-do-good-footer'
                            : 'philanthropy-shine-bright-do-good-footer'
                    }
                    skeleton={philanthropySkeleton}
                />
            </Box>
            <Box className="social-icons-wrapper" display={{base: 'none', lg: 'block'}}>
                <ContentAsset
                    contentAssetId={isYR ? 'yr-footer-social-icons' : 'footer-social-icons'}
                    skeleton={socialIconsSkeleton}
                />
            </Box>
            {geEnabled && (
                <Box display={{base: 'none', lg: 'block'}}>
                    <CountrySelector countryData={countryData} />
                </Box>
            )}
        </Box>
    )
}

Subscribe.propTypes = {
    countryData: PropTypes.any,
    geEnabled: PropTypes.bool,
    isYR: PropTypes.bool
}

CountrySelector.propTypes = {
    countryData: PropTypes.any
}
